import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Calendar, Clock, User } from 'lucide-react';
import { blogPosts } from '../mock';

const BlogPost = () => {
  const { id } = useParams();
  const post = blogPosts.find(p => p.id === parseInt(id));

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <Link to="/blog">
            <Button className="bg-amber-600 hover:bg-amber-700">
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="pt-32 pb-12 px-6 lg:px-8 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog">
            <Button variant="ghost" className="mb-6 hover:text-amber-600">
              <ArrowLeft className="mr-2" size={20} />
              Back to Blog
            </Button>
          </Link>
          
          <Badge className="bg-amber-100 text-amber-700 mb-4">
            {post.category}
          </Badge>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-gray-600">
            <div className="flex items-center space-x-2">
              <User size={18} />
              <span>Pratish Jain</span>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar size={18} />
              <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock size={18} />
              <span>{post.readTime}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          {/* Featured Image Placeholder */}
          <div className="bg-gradient-to-br from-amber-100 to-amber-200 rounded-2xl h-96 mb-12 flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl font-bold text-amber-600 mb-4">{post.category}</div>
              <p className="text-amber-700 text-lg">Featured Image</p>
            </div>
          </div>

          {/* Article Content */}
          <article className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              {post.excerpt}
            </p>

            <div className="bg-amber-50 border-l-4 border-amber-600 p-6 my-8">
              <p className="text-gray-700 italic">
                <strong>Note:</strong> This is a placeholder blog post. In the full version, this would contain the complete article with detailed insights, examples, and actionable strategies for B2B SaaS content marketing and SEO.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Coming Soon</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              This blog section is currently being developed. Stay tuned for in-depth articles on:
            </p>
            
            <ul className="space-y-3 mb-8">
              <li className="text-gray-700">• SEO content strategies that drive qualified leads</li>
              <li className="text-gray-700">• B2B SaaS content marketing best practices</li>
              <li className="text-gray-700">• Lead generation through content assets</li>
              <li className="text-gray-700">• Analytics-driven content optimization</li>
              <li className="text-gray-700">• Conversion rate optimization for B2B websites</li>
            </ul>

            <p className="text-gray-700 leading-relaxed">
              In the meantime, feel free to check out my portfolio to see real results from content strategies I've implemented for companies like Engati and helo.ai.
            </p>
          </article>

          {/* CTA Section */}
          <div className="mt-16 p-8 bg-gradient-to-br from-amber-50 to-white rounded-2xl border-2 border-amber-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Want content strategies for your SaaS?
            </h3>
            <p className="text-gray-700 mb-6">
              Let's discuss how to build a content engine that drives qualified leads for your B2B SaaS.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="mailto:thepratishjain@gmail.com">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">
                  Get in Touch
                </Button>
              </a>
              <Link to="/portfolio">
                <Button size="lg" variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-50">
                  View Portfolio
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
